import React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import { Drawer, Empty, Space, Spin, Table, Tag, Typography, notification } from 'antd';
import { DesignerAvatar } from '../common/designerAvatar';
import { ChallengesProxy } from '../services/catalogServiceAPI/challengesProxy';
import { RENDERINGS_BASE } from './constants';

const POLL_INTERVAL_MS = 10000;

@observer
export class SubmissionsDrawer extends React.Component {
    challengesProxy = new ChallengesProxy();

    @observable submissions = [];
    @observable isLoading = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    @action.bound setIsLoading = (v) => this.isLoading = v;
    @action.bound setSubmissions = (v) => this.submissions.replace(v);

    componentDidMount() {
        if (this.props.challenge) {
            this.fetch();
            this.startPollingIfLive();
        }
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.challenge?.id;
        const currId = this.props.challenge?.id;
        if (prevId !== currId) {
            this.stopPolling();
            this.setSubmissions([]);
            if (this.props.challenge) {
                this.fetch();
                this.startPollingIfLive();
            }
        } else if (this.props.challenge && prevProps.challenge?.status !== this.props.challenge.status) {
            this.stopPolling();
            this.startPollingIfLive();
        }
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    startPollingIfLive() {
        if (this.props.challenge?.status !== 'active') return;
        this.pollTimer = setInterval(() => {
            if (document.visibilityState === 'visible') this.fetch({ silent: true });
        }, POLL_INTERVAL_MS);
    }

    stopPolling() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    async fetch({ silent = false } = {}) {
        const id = this.props.challenge?.id;
        if (!id) return;
        if (!silent) this.setIsLoading(true);
        try {
            const submissions = await this.challengesProxy.listSubmissions(id);
            this.setSubmissions(submissions);
        } catch (e) {
            if (!silent) notification.error({ message: 'Failed to load submissions', description: e.message });
        } finally {
            if (!silent) this.setIsLoading(false);
        }
    }

    columns = [
        {
            title: 'Thumbnail',
            dataIndex: 'renderingId',
            width: 96,
            render: (renderingId) => (
                <a
                    href={`https://designwithfriends.com/render/${renderingId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img
                        src={`${RENDERINGS_BASE}/${renderingId}`}
                        alt=""
                        style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                    />
                </a>
            ),
        },
        {
            title: 'Designer',
            dataIndex: 'userName',
            render: (userName, row) => (
                <Space>
                    <DesignerAvatar avatar={row.avatar} photoURL={row.userAvatar} />
                    <Space direction="vertical" size={0}>
                        <span>{userName}</span>
                        <Typography.Text type="secondary" copyable style={{ fontSize: 12 }}>{row.userId}</Typography.Text>
                    </Space>
                </Space>
            ),
        },
        {
            title: 'Submitted',
            dataIndex: 'submittedAt',
            width: 180,
            render: (v) => v ? new Date(v).toLocaleString() : '—',
        },
    ];

    render() {
        const { challenge, open, onClose } = this.props;
        const isLive = challenge?.status === 'active';
        return (
            <Drawer
                title={
                    <Space>
                        <span>Submissions{challenge ? ` — ${challenge.title}` : ''}</span>
                        {isLive && <Tag color="green">LIVE</Tag>}
                    </Space>
                }
                width={720}
                placement="right"
                open={open}
                onClose={onClose}
                destroyOnClose
            >
                {this.isLoading ? (
                    <Spin />
                ) : this.submissions.length === 0 ? (
                    <Empty description="No submissions yet" />
                ) : (
                    <>
                        <Typography.Text type="secondary">
                            {this.submissions.length} submission{this.submissions.length === 1 ? '' : 's'}
                        </Typography.Text>
                        <Table
                            rowKey="submissionId"
                            dataSource={this.submissions.slice()}
                            columns={this.columns}
                            pagination={{ pageSize: 20 }}
                            style={{ marginTop: 12 }}
                        />
                    </>
                )}
            </Drawer>
        );
    }
}
